import { useState, useEffect, useCallback } from "react";
import { AdminCard, AdminCardHead, LoadFailedBanner } from "components/admin/ui";
import { adminGet, adminPut } from "lib/adminClient";

const COLORS = [
  ["#005C40", "Green"],
  ["#8B1A4A", "Rose"],
  ["#9B7200", "Gold"],
  ["#B8002B", "Red"],
  ["#003d2b", "Dark Green"],
  ["#2d6ab0", "Blue"],
];

const inputCls =
  "w-full px-3 py-2 border-[1.5px] border-line rounded-lg font-sans text-sm outline-none focus:border-green";
const labelCls = "text-[12px] font-semibold text-muted block mb-1.5";
const addBtn =
  "bg-green text-white border-none rounded-lg px-3.5 py-1.5 font-sans font-semibold text-xs cursor-pointer hover:opacity-90";
const subBtn =
  "bg-green-light text-green-dark border-none rounded-md px-2.5 py-1 font-sans font-semibold text-[11px] cursor-pointer hover:opacity-80";
const delBtn =
  "bg-[#fdeaea] border-none rounded-md px-2.5 py-2 cursor-pointer text-xs text-[#c44] shrink-0";

const blankGroup = () => ({ name: "", icon: "📘", color: "#005C40", subs: [], variant: "" });
const blankSection = () => ({ title: "", groups: [blankGroup()] });
const blankTab = () => ({
  key: `tab${Date.now()}`,
  label: "",
  kicker: "",
  color: "#005C40",
  intro: "",
  sections: [blankSection()],
});
const blankFacility = () => ({ icon: "🏫", name: "", desc: "" });

function Field({ label, hint, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <span className="text-[11.5px] text-muted mt-1 block">{hint}</span>}
    </div>
  );
}

function GroupRow({ group, onChange, onRemove }) {
  const set = (f, v) => onChange({ ...group, [f]: v });
  return (
    <div className="border border-line rounded-lg p-3 grid grid-cols-1 md:grid-cols-[56px_1.2fr_1fr_130px_auto] gap-2.5 items-start">
      <input
        value={group.icon}
        onChange={(e) => set("icon", e.target.value)}
        placeholder="📘"
        className={`${inputCls} text-center`}
      />
      <input
        value={group.name}
        onChange={(e) => set("name", e.target.value)}
        placeholder="Group name"
        className={inputCls}
      />
      <input
        value={(group.subs || []).join(", ")}
        onChange={(e) =>
          set("subs", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))
        }
        placeholder="Subjects, comma separated"
        className={inputCls}
      />
      <select
        value={group.color}
        onChange={(e) => set("color", e.target.value)}
        className={`${inputCls} bg-white`}
      >
        {COLORS.map(([hex, n]) => (
          <option key={hex} value={hex}>
            {n}
          </option>
        ))}
      </select>
      <div className="flex gap-1.5">
        <label
          title="Wide list card — one divided row per entry"
          className="flex items-center gap-1 text-[11px] text-muted cursor-pointer border border-line rounded-md px-2"
        >
          <input
            type="checkbox"
            checked={group.variant === "list"}
            onChange={(e) => set("variant", e.target.checked ? "list" : "")}
            className="cursor-pointer"
          />
          List
        </label>
        <button onClick={onRemove} title="Remove group" className={delBtn}>
          🗑
        </button>
      </div>
    </div>
  );
}

function TabEditor({ tab, onChange, onRemove }) {
  const set = (f, v) => onChange({ ...tab, [f]: v });
  const sections = tab.sections || [];
  const setSection = (i, next) =>
    set("sections", sections.map((s, idx) => (idx === i ? next : s)));

  return (
    <div className="border-[1.5px] rounded-xl p-4" style={{ borderColor: tab.color }}>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr_150px_auto] gap-3 mb-3">
        <Field label="Tab label">
          <input
            value={tab.label}
            onChange={(e) => set("label", e.target.value)}
            placeholder="KCGS"
            className={inputCls}
          />
        </Field>
        <Field label="Kicker">
          <input
            value={tab.kicker}
            onChange={(e) => set("kicker", e.target.value)}
            placeholder="School & College Section"
            className={inputCls}
          />
        </Field>
        <Field label="Colour">
          <select
            value={tab.color}
            onChange={(e) => set("color", e.target.value)}
            className={`${inputCls} bg-white`}
          >
            {COLORS.map(([hex, n]) => (
              <option key={hex} value={hex}>
                {n}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex items-end">
          <button onClick={onRemove} title="Remove tab" className={delBtn}>
            🗑 Tab
          </button>
        </div>
      </div>

      <div className="mb-4">
        <Field label="Intro paragraph">
          <textarea
            value={tab.intro}
            onChange={(e) => set("intro", e.target.value)}
            rows={2}
            className={`${inputCls} resize-y`}
          />
        </Field>
      </div>

      {sections.map((sec, si) => (
        <div key={si} className="border-l-2 border-line pl-3.5 mb-3.5">
          <div className="flex items-end gap-2 mb-2.5">
            <div className="flex-1">
              <Field label="Sub-section title" hint="Leave blank for an untitled group of cards.">
                <input
                  value={sec.title}
                  onChange={(e) => setSection(si, { ...sec, title: e.target.value })}
                  placeholder="School — Class VI–X / SSC"
                  className={inputCls}
                />
              </Field>
            </div>
            <button
              onClick={() =>
                setSection(si, { ...sec, groups: [...(sec.groups || []), blankGroup()] })
              }
              className={subBtn}
            >
              + Card
            </button>
            <button
              onClick={() => set("sections", sections.filter((_, idx) => idx !== si))}
              title="Remove sub-section"
              className={delBtn}
            >
              🗑
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {(sec.groups || []).map((g, gi) => (
              <GroupRow
                key={gi}
                group={g}
                onChange={(next) =>
                  setSection(si, {
                    ...sec,
                    groups: sec.groups.map((x, idx) => (idx === gi ? next : x)),
                  })
                }
                onRemove={() =>
                  setSection(si, {
                    ...sec,
                    groups: sec.groups.filter((_, idx) => idx !== gi),
                  })
                }
              />
            ))}
          </div>
        </div>
      ))}

      <button onClick={() => set("sections", [...sections, blankSection()])} className={subBtn}>
        + Add sub-section
      </button>
    </div>
  );
}

export default function AcademicsPage() {
  const [heroTitle, setHeroTitle] = useState("");
  const [heroSub, setHeroSub] = useState("");
  const [tabs, setTabs] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [migrated, setMigrated] = useState(false);

  const apply = (d) => {
    setHeroTitle(d.hero?.title || "");
    setHeroSub(d.hero?.sub || "");
    setFacilities(d.facilities || []);

    if (Array.isArray(d.tabs)) {
      setTabs(d.tabs);
      setMigrated(false);
      return;
    }
    // Legacy { school, college } blob — lift it into the tabbed shape so the
    // editor can save it forward without losing anything.
    const sections = [
      { title: "School — Class VI–X / SSC", groups: d.school || [] },
      { title: "College — HSC & Honours", groups: d.college || [] },
    ].filter((s) => s.groups.length);
    setTabs(
      sections.length
        ? [
            {
              key: "kcgs",
              label: "KCGS",
              kicker: "School & College Section",
              color: "#8B1A4A",
              intro: "",
              sections,
            },
          ]
        : []
    );
    setMigrated(sections.length > 0);
  };

  const [loadFailed, setLoadFailed] = useState(false);


  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setError("");
    try {
      apply((await adminGet("/admin/content/academics")) || {});
    } catch (e) {
      setLoadFailed(true);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updFac = (i, f, v) =>
    setFacilities(facilities.map((x, idx) => (idx === i ? { ...x, [f]: v } : x)));

  const save = async () => {
    if (loadFailed) {
      setError("Content didn't load, so saving now would erase it. Reload the page first.");
      return;
    }
    setSaving(true);
    setStatus("");
    setError("");
    try {
      const saved = await adminPut("/admin/content/academics", {
        hero: { title: heroTitle.trim(), sub: heroSub.trim() },
        tabs: tabs
          .filter((t) => (t.label || "").trim())
          .map((t) => ({
            ...t,
            sections: (t.sections || []).map((s) => ({
              ...s,
              groups: (s.groups || []).filter((g) => (g.name || "").trim()),
            })),
          })),
        facilities: facilities.filter((f) => (f.name || "").trim()),
      });
      if (saved) apply(saved);
      setStatus("Saved.");
      setTimeout(() => setStatus(""), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="px-1 py-8 text-muted text-sm">Loading…</div>;

  return (
    <div className="animate-fadeIn">
      {loadFailed && <LoadFailedBanner onRetry={load} />}

      {error && (
        <div className="text-[13px] text-red bg-red/[0.08] rounded-lg px-4 py-2.5 mb-4">{error}</div>
      )}
      {migrated && (
        <div className="text-[13px] text-[#8a6d00] bg-gold/[0.12] rounded-lg px-4 py-2.5 mb-4">
          This page still uses the old School/College format. It's been lifted into the new
          tabbed layout below — press <strong>Save Changes</strong> to store it in the new
          format, then add your KCNS and Honors tabs.
        </div>
      )}

      <AdminCard className="mb-[18px]">
        <AdminCardHead title="Page Header" />
        <div className="p-[22px] grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <Field label="Title">
            <input
              value={heroTitle}
              onChange={(e) => setHeroTitle(e.target.value)}
              placeholder="Academics"
              className={inputCls}
            />
          </Field>
          <Field label="Sub-heading">
            <input
              value={heroSub}
              onChange={(e) => setHeroSub(e.target.value)}
              placeholder="One campus, three institutions…"
              className={inputCls}
            />
          </Field>
        </div>
      </AdminCard>

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Tabs"
          action={
            <button onClick={() => setTabs([...tabs, blankTab()])} className={addBtn}>
              + Add Tab
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-4">
          {tabs.length === 0 && <div className="text-muted text-sm">No tabs yet.</div>}
          {tabs.map((t, i) => (
            <TabEditor
              key={t.key || i}
              tab={t}
              onChange={(next) => setTabs(tabs.map((x, idx) => (idx === i ? next : x)))}
              onRemove={() => setTabs(tabs.filter((_, idx) => idx !== i))}
            />
          ))}
        </div>
      </AdminCard>

      <AdminCard className="mb-[18px]">
        <AdminCardHead
          title="Facilities"
          action={
            <button onClick={() => setFacilities([...facilities, blankFacility()])} className={addBtn}>
              + Add Facility
            </button>
          }
        />
        <div className="p-[22px] flex flex-col gap-3">
          {facilities.length === 0 && <div className="text-muted text-sm">No facilities yet.</div>}
          {facilities.map((f, i) => (
            <div
              key={i}
              className="border border-line rounded-xl p-3.5 grid grid-cols-1 md:grid-cols-[60px_1fr_2fr_auto] gap-3 items-start"
            >
              <input
                value={f.icon}
                onChange={(e) => updFac(i, "icon", e.target.value)}
                className={`${inputCls} text-center`}
              />
              <input
                value={f.name}
                onChange={(e) => updFac(i, "name", e.target.value)}
                placeholder="Facility name"
                className={inputCls}
              />
              <input
                value={f.desc}
                onChange={(e) => updFac(i, "desc", e.target.value)}
                placeholder="Short description"
                className={inputCls}
              />
              <button
                onClick={() => setFacilities(facilities.filter((_, idx) => idx !== i))}
                title="Remove"
                className={delBtn}
              >
                🗑
              </button>
            </div>
          ))}
        </div>
      </AdminCard>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="bg-green text-white border-none rounded-[9px] px-[26px] py-[11px] font-sans font-bold text-sm cursor-pointer hover:opacity-90 transition-opacity disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save Changes"}
        </button>
        {status && <span className="text-[13px] text-[#1a9f5e] font-semibold">{status}</span>}
      </div>
    </div>
  );
}
